import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Paper,
  TableHead,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import style from "../../../../styles/brsr/general.module.css";
import EastIcon from "@mui/icons-material/East";
import { put, api, get } from "../../../../utils/api";
import { useParams } from "react-router-dom";
import { brsrReview, reviewBRSR } from "../../../../utils/companyId";
import ReviewBox from "../../../Common/ReviewBox";
import { currentYear } from "../../../../utils/generateYearRanges";
import BRSRFile from "../../../Common/BRSRFile";

const stakeholderLabels = {
  investors: "Investors",
  shareholders: "Shareholders",
  customers: "Customers",
  businessPartners: "Business Partners",
  employeesAndWorkers: "Employees & Workers",
  regulatoryBodies: "Regulatory Bodies",
  tradeBodies: "Trade bodies & other organizations",
  localCommunity: "Local community",
};

const CustomTableRow = ({
  label,
  fields,
  formData,
  handleInputChange,
  showTotal,
  placeholderMap,
  errors,
  disabled,
}) => {
  const [showOtherChannelInput, setShowOtherChannelInput] = useState(false);
  const [showOtherFrequencyInput, setShowOtherFrequencyInput] = useState(false);

  useEffect(() => {
    setShowOtherChannelInput(formData.channelOfCommunication === "Others");
    setShowOtherFrequencyInput(formData.frequency === "Others");
  }, [formData.channelOfCommunication, formData.frequency]);

  return (
    <TableRow sx={{ height: "auto" }}>
      <TableCell>{stakeholderLabels[label]}</TableCell>
      {fields.map((field) => (
        <TableCell key={field}>
          {field === "wheatherIdentified" ? (
            <FormControl fullWidth error={!!errors[`${label}.${field}`]}>
              <Select
                value={formData[field] || ""}
                name={`${label}.${field}`}
                onChange={handleInputChange}
                disabled={disabled}
                displayEmpty
                sx={{
                  border: "none",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                <MenuItem value="" disabled>
                  Select
                </MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
              {errors[`${label}.${field}`] && (
                <Typography color="error" variant="caption">
                  {errors[`${label}.${field}`]}
                </Typography>
              )}
            </FormControl>
          ) : field === "channelOfCommunication" ? (
            <Box>
              <FormControl fullWidth error={!!errors[`${label}.${field}`]}>
                <Select
                  value={formData[field] || ""}
                  disabled={disabled}
                  name={`${label}.${field}`}
                  onChange={handleInputChange}
                  displayEmpty
                  sx={{
                    border: "none",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select
                  </MenuItem>
                  <MenuItem value="Email">Email</MenuItem>
                  <MenuItem value="SMS">SMS</MenuItem>
                  <MenuItem value="Newspaper">Newspaper</MenuItem>
                  <MenuItem value="Pamphlets">Pamphlets</MenuItem>
                  <MenuItem value="Advertisement">Advertisement</MenuItem>
                  <MenuItem value="Community Meetings">
                    Community Meetings
                  </MenuItem>
                  <MenuItem value="Notice Board">Notice Board</MenuItem>
                  <MenuItem value="Website">Website</MenuItem>
                  <MenuItem value="Others">Others</MenuItem>
                </Select>
              </FormControl>
              {showOtherChannelInput && (
                <TextField
                  disabled={disabled}
                  name={`${label}.newChannelOfCommunication`} // Update this field
                  value={formData.newChannelOfCommunication || ""}
                  onChange={handleInputChange}
                  size="small"
                  placeholder="Please specify"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    mt: 1,
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                  multiline
                  minRows={1}
                  error={!!errors[label]?.newChannelOfCommunication}
                  helperText={errors[label]?.newChannelOfCommunication}
                />
              )}
            </Box>
          ) : field === "frequency" ? (
            <Box>
              <FormControl fullWidth error={!!errors[`${label}.${field}`]}>
                <Select
                  disabled={disabled}
                  value={formData[field] || ""}
                  name={`${label}.${field}`}
                  onChange={handleInputChange}
                  displayEmpty
                  sx={{
                    border: "none",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select
                  </MenuItem>
                  <MenuItem value="Annually">Annually</MenuItem>
                  <MenuItem value="Half yearly">Half yearly</MenuItem>
                  <MenuItem value="Quarterly">Quarterly</MenuItem>
                  <MenuItem value="Others">Others</MenuItem>
                </Select>
              </FormControl>
              {showOtherFrequencyInput && (
                <TextField
                  disabled={disabled}
                  name={`${label}.newFrequency`} // Update this field
                  value={formData.newFrequency || ""}
                  onChange={handleInputChange}
                  size="small"
                  placeholder="Please specify"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    mt: 1,
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                  multiline
                  minRows={1}
                  error={!!errors[label]?.newFrequency}
                  helperText={errors[label]?.newFrequency}
                />
              )}
            </Box>
          ) : (
            <Box>
              <TextField
                disabled={disabled}
                name={`${label}.${field}`}
                value={formData[field] || ""}
                onChange={handleInputChange}
                size="small"
                placeholder={placeholderMap[field] || "Type Here"}
                InputProps={{ disableUnderline: true }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
                error={!!errors[field]}
                multiline
                minRows={1}
              />
            </Box>
          )}
        </TableCell>
      ))}
    </TableRow>
  );
};

const EssentialIndicatorsForm = ({
  onSave,
  hide,
  removeUpload = false,
  downloadMode = false,
}) => {
  const { id } = useParams();
  const [modalupload, setModalUpload] = useState({
    stakeholderGroupDoc: false,
    identificationOfStakeholdersDoc: false,
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [progress, setUploadProgress] = useState([]);
  const [formData1, setFormData1] = useState({});
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [stakeholders, setStakeholders] = useState({
    processDescription: "",
    investors: {
      wheatherIdentified: "",
      channelOfCommunication: "",
      frequency: "",
      purpose: "",
    },
    shareholders: {
      wheatherIdentified: "",
      channelOfCommunication: "",
      frequency: "",
      purpose: "",
    },
    customers: {
      wheatherIdentified: "",
      channelOfCommunication: "",
      frequency: "",
      purpose: "",
    },
    businessPartners: {
      wheatherIdentified: "",
      channelOfCommunication: "",
      frequency: "",
      purpose: "",
    },
    employeesAndWorkers: {
      wheatherIdentified: "",
      channelOfCommunication: "",
      frequency: "",
      purpose: "",
    },
    regulatoryBodies: {
      wheatherIdentified: "",
      channelOfCommunication: "",
      frequency: "",
      purpose: "",
    },
    tradeBodies: {
      wheatherIdentified: "",
      channelOfCommunication: "",
      frequency: "",
      purpose: "",
    },
    localCommunity: {
      wheatherIdentified: "",
      channelOfCommunication: "",
      frequency: "",
      purpose: "",
    },
  });

  const [errors, setErrors] = useState({});
  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const type = "SectionC";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(
          `${api}/brsr/get?subType=principle4&type=${type}&id=${id}`,
        );
        if (
          response?.data?.data?.year
            ? response?.data?.data?.year < currentYear - 1
            : false || !brsrReview
        )
          setDisabled(true);

        if (
          response &&
          response.data &&
          response.data.data.BRSRSectionC &&
          response.data.data.BRSRSectionC.principle4.essentialIndicators
        ) {
          const essentialIndicators =
            response?.data?.data?.BRSRSectionC?.principle4?.essentialIndicators;

          setStakeholders((prevStakeholders) => ({
            ...prevStakeholders,
            processDescription: essentialIndicators?.stakeholderGroup ?? "", // Setting processDescription (stakeholderGroup in response)
            investors: {
              wheatherIdentified:
                essentialIndicators?.identificationOfStakeholders?.investors
                  ?.wheatherIdentified ?? "",
              channelOfCommunication:
                essentialIndicators?.identificationOfStakeholders?.investors
                  ?.channelOfCommunication ?? "",
              frequency:
                essentialIndicators?.identificationOfStakeholders?.investors
                  ?.frequency ?? "",
              newChannelOfCommunication:
                essentialIndicators?.identificationOfStakeholders?.investors
                  ?.newChannelOfCommunication ?? "",
              newFrequency:
                essentialIndicators?.identificationOfStakeholders?.investors
                  ?.newFrequency ?? "",
              purpose:
                essentialIndicators?.identificationOfStakeholders?.investors
                  ?.purpose ?? "",
            },
            shareholders: {
              wheatherIdentified:
                essentialIndicators?.identificationOfStakeholders?.shareholders
                  ?.wheatherIdentified ?? "",
              channelOfCommunication:
                essentialIndicators?.identificationOfStakeholders?.shareholders
                  ?.channelOfCommunication ?? "",
              frequency:
                essentialIndicators?.identificationOfStakeholders?.shareholders
                  ?.frequency ?? "",
              purpose:
                essentialIndicators?.identificationOfStakeholders?.shareholders
                  ?.purpose ?? "",
              newChannelOfCommunication:
                essentialIndicators?.identificationOfStakeholders?.shareholders
                  ?.newChannelOfCommunication ?? "",
              newFrequency:
                essentialIndicators?.identificationOfStakeholders?.shareholders
                  ?.newFrequency ?? "",
            },
            customers: {
              wheatherIdentified:
                essentialIndicators?.identificationOfStakeholders?.customers
                  ?.wheatherIdentified ?? "",
              channelOfCommunication:
                essentialIndicators?.identificationOfStakeholders?.customers
                  ?.channelOfCommunication ?? "",
              frequency:
                essentialIndicators?.identificationOfStakeholders?.customers
                  ?.frequency ?? "",
              purpose:
                essentialIndicators?.identificationOfStakeholders?.customers
                  ?.purpose ?? "",
              newChannelOfCommunication:
                essentialIndicators?.identificationOfStakeholders?.customers
                  ?.newChannelOfCommunication ?? "",
              newFrequency:
                essentialIndicators?.identificationOfStakeholders?.customers
                  ?.newFrequency ?? "",
            },
            businessPartners: {
              wheatherIdentified:
                essentialIndicators?.identificationOfStakeholders
                  ?.businessPartners?.wheatherIdentified ?? "",
              channelOfCommunication:
                essentialIndicators?.identificationOfStakeholders
                  ?.businessPartners?.channelOfCommunication ?? "",
              frequency:
                essentialIndicators?.identificationOfStakeholders
                  ?.businessPartners?.frequency ?? "",
              purpose:
                essentialIndicators?.identificationOfStakeholders
                  ?.businessPartners?.purpose ?? "",
              newChannelOfCommunication:
                essentialIndicators?.identificationOfStakeholders
                  ?.businessPartners?.newChannelOfCommunication ?? "",
              newFrequency:
                essentialIndicators?.identificationOfStakeholders
                  ?.businessPartners?.newFrequency ?? "",
            },
            employeesAndWorkers: {
              wheatherIdentified:
                essentialIndicators?.identificationOfStakeholders
                  ?.employeesAndWorkers?.wheatherIdentified ?? "",
              channelOfCommunication:
                essentialIndicators?.identificationOfStakeholders
                  ?.employeesAndWorkers?.channelOfCommunication ?? "",
              frequency:
                essentialIndicators?.identificationOfStakeholders
                  ?.employeesAndWorkers?.frequency ?? "",
              purpose:
                essentialIndicators?.identificationOfStakeholders
                  ?.employeesAndWorkers?.purpose ?? "",
              newChannelOfCommunication:
                essentialIndicators?.identificationOfStakeholders
                  ?.employeesAndWorkers?.newChannelOfCommunication ?? "",
              newFrequency:
                essentialIndicators?.identificationOfStakeholders
                  ?.employeesAndWorkers?.newFrequency ?? "",
            },
            regulatoryBodies: {
              wheatherIdentified:
                essentialIndicators?.identificationOfStakeholders
                  ?.regulatoryBodies?.wheatherIdentified ?? "",
              channelOfCommunication:
                essentialIndicators?.identificationOfStakeholders
                  ?.regulatoryBodies?.channelOfCommunication ?? "",
              frequency:
                essentialIndicators?.identificationOfStakeholders
                  ?.regulatoryBodies?.frequency ?? "",
              purpose:
                essentialIndicators?.identificationOfStakeholders
                  ?.regulatoryBodies?.purpose ?? "",
              newChannelOfCommunication:
                essentialIndicators?.identificationOfStakeholders
                  ?.regulatoryBodies?.newChannelOfCommunication ?? "",
              newFrequency:
                essentialIndicators?.identificationOfStakeholders
                  ?.regulatoryBodies?.newFrequency ?? "",
            },
            tradeBodies: {
              wheatherIdentified:
                essentialIndicators?.identificationOfStakeholders?.tradeBodies
                  ?.wheatherIdentified ?? "",
              channelOfCommunication:
                essentialIndicators?.identificationOfStakeholders?.tradeBodies
                  ?.channelOfCommunication ?? "",
              frequency:
                essentialIndicators?.identificationOfStakeholders?.tradeBodies
                  ?.frequency ?? "",
              purpose:
                essentialIndicators?.identificationOfStakeholders?.tradeBodies
                  ?.purpose ?? "",
              newChannelOfCommunication:
                essentialIndicators?.identificationOfStakeholders?.tradeBodies
                  ?.newChannelOfCommunication ?? "",
              newFrequency:
                essentialIndicators?.identificationOfStakeholders?.tradeBodies
                  ?.newFrequency ?? "",
            },
            localCommunity: {
              wheatherIdentified:
                essentialIndicators?.identificationOfStakeholders
                  ?.localCommunity?.wheatherIdentified ?? "",
              channelOfCommunication:
                essentialIndicators?.identificationOfStakeholders
                  ?.localCommunity?.channelOfCommunication ?? "",
              frequency:
                essentialIndicators?.identificationOfStakeholders
                  ?.localCommunity?.frequency ?? "",
              purpose:
                essentialIndicators?.identificationOfStakeholders
                  ?.localCommunity?.purpose ?? "",
              newChannelOfCommunication:
                essentialIndicators?.identificationOfStakeholders
                  ?.localCommunity?.newChannelOfCommunication ?? "",
              newFrequency:
                essentialIndicators?.identificationOfStakeholders
                  ?.localCommunity?.newFrequency ?? "",
            },
          }));
        } else {
          console.error("Essential Indicators data is not available.");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [id, type]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const [stakeholder, field] = name.split(".");

    // Update the stakeholders state
    setStakeholders((prevState) => ({
      ...prevState,
      [stakeholder]: {
        ...prevState[stakeholder],
        [field]: value,
      },
    }));

    // Clear the specific error for the updated field
    setErrors((prevErrors) => {
      if (prevErrors[stakeholder] && prevErrors[stakeholder][field]) {
        const updatedStakeholderErrors = { ...prevErrors[stakeholder] };
        delete updatedStakeholderErrors[field];

        // If no other errors remain for the stakeholder, remove it entirely
        if (Object.keys(updatedStakeholderErrors).length === 0) {
          const { [stakeholder]: _, ...rest } = prevErrors;
          return rest;
        }

        return { ...prevErrors, [stakeholder]: updatedStakeholderErrors };
      }
      return prevErrors;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setStakeholders((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Clear the error for this field when the user starts typing
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };

      // If this field has an error and the user starts typing, remove the error
      if (updatedErrors[name] && value.trim() !== "") {
        delete updatedErrors[name];
      }

      return updatedErrors;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate process description
    if (
      !stakeholders.processDescription ||
      stakeholders.processDescription.trim() === ""
    ) {
      newErrors["processDescription"] = "Process description is required.";
    }

    // Validate stakeholder fields
    Object.keys(stakeholders).forEach((stakeholder) => {
      if (stakeholder === "processDescription") return; // Skip process description validation

      const stakeholderFields = stakeholders[stakeholder];

      // Check if any field in the row has data
      const hasData = Object.values(stakeholderFields).some(
        (value) => value && value.trim() !== "",
      );

      if (hasData) {
        Object.keys(stakeholderFields).forEach((field) => {
          if (
            field === "newChannelOfCommunication" &&
            stakeholderFields.channelOfCommunication !== "Others"
          ) {
            return;
          }
          if (
            field === "newFrequency" &&
            stakeholderFields.frequency !== "Others"
          ) {
            return;
          }

          // Validate required fields if the row has data
          if (
            !stakeholderFields[field] ||
            stakeholderFields[field].trim() === ""
          ) {
            if (!newErrors[stakeholder]) newErrors[stakeholder] = {};
            newErrors[stakeholder][field] =
              `${stakeholderLabels[stakeholder]} - ${field} is required`;
          }
        });
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      if (!validateForm()) {
        return;
      }
      const payload = {
        essentialIndicators: {
          stakeholderGroup: stakeholders.processDescription,
          identificationOfStakeholders: stakeholders,
          ...formData1,
        },
        type: "SectionC",
        subType: "principle4",
        essentialIndicatorsSubmitted: true,
      };

      try {
        const response = await put(`/brsr/update?id=${id}`, payload);
        setsnackOpen({
          open: true,
          message: "Data saved successfully!",
          severity: "success",
        });
        if (onSave) onSave(response.data);
      } catch (error) {
        console.error(error);
        setsnackOpen({
          open: true,
          message: "Failed to save data. Please try again.",
          severity: "error",
        });
      }
    } else onSave();
  };

  return (
    <Box>
      <p className={style.subHeading}>I. ESSENTIAL INDICATOR</p>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography sx={{ fontWeight: 600, fontSize: "16px" }} gutterBottom>
            1. Describe the processes for identifying key stakeholder groups of
            the entity.
          </Typography>
          <TextField
            fullWidth
            disabled={disabled}
            multiline
            minRows={4}
            sx={{ marginLeft: "0.9rem", width: "99%" }}
            placeholder="Type Here"
            variant="outlined"
            value={stakeholders.processDescription || ""}
            onChange={handleChange}
            name="processDescription"
            error={!!errors["processDescription"]}
            helperText={
              errors["processDescription"] ? (
                <Typography
                  variant="body2"
                  color="error"
                  style={{
                    lineHeight: "1.2",
                    margin: "2px 0",
                    color: "#d34040",
                    fontSize: "10px",
                    fontStyle: "italic",
                    marginLeft: "-10px",
                  }}
                >
                  {errors["processDescription"]}
                </Typography>
              ) : null
            }
          />
        </Grid>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData: formData1,
            setFormData: setFormData1,
            errors,
            setErrors,
            open,
            setOpen,
            Uploadkey: "stakeholderGroupDoc",
            ml: 5,
            removeUpload,
          }}
        />
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <Grid item xs={12}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              mt: 2,
              textIndent: "-1rem",
              paddingLeft: "0.9rem",
            }}
            gutterBottom
          >
            2. List stakeholder groups identified as key for your entity and the
            frequency of engagement with each stakeholder group.
          </Typography>

          {/* Table for Stakeholder Groups */}
          <TableContainer
            component={Paper}
            sx={{ borderRadius: "10px", marginLeft: "0.9rem", width: "99%" }}
          >
            <Table
              sx={{
                "& .MuiTableCell-root": {
                  borderRight: "1px solid #e0e0e0",
                  padding: "0px 16px",
                },
                tableLayout: "fixed",
                width: "100%",
              }}
            >
              <TableHead
                sx={{
                  "& .MuiTableCell-root": {
                    padding: "10px 16px",
                  },
                }}
              >
                <TableRow sx={{ bgcolor: "#1C486B", height: "50px" }}>
                  <TableCell
                    sx={{
                      color: "white",
                      borderBottom: "1px solid white",
                      width: "19%",
                    }}
                  >
                    Stakeholder Group
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      borderBottom: "1px solid white",
                      width: "16%",
                    }}
                  >
                    Whether identified as Vulnerable & Marginalised Group
                    (Yes/No)
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      borderBottom: "1px solid white",
                      width: "25%",
                    }}
                  >
                    Channels of communication (Email, SMS, Newspaper, Pamphlets,
                    Advertisement, Community Meetings, Notice Board, Website),
                    Others
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      borderBottom: "1px solid white",
                      width: "20%",
                    }}
                  >
                    Frequency of engagement (Annually/ Half yearly/ Quarterly /
                    others please specify)
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      borderBottom: "1px solid white",
                      width: "20%",
                    }}
                  >
                    Purpose and scope of engagement including key topics and
                    concerns raised during such engagement
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  "investors",
                  "shareholders",
                  "customers",
                  "businessPartners",
                  "employeesAndWorkers",
                  "regulatoryBodies",
                  "tradeBodies",
                  "localCommunity",
                ].map((stakeholder) => (
                  <CustomTableRow
                    key={stakeholder}
                    label={stakeholder}
                    disabled={disabled}
                    fields={[
                      "wheatherIdentified",
                      "channelOfCommunication",
                      "frequency",
                      "purpose",
                    ]}
                    formData={stakeholders[stakeholder]}
                    handleInputChange={handleInputChange}
                    showTotal={stakeholder === "R&D" || stakeholder === "Capex"}
                    placeholderMap={{
                      channelOfCommunication: "Enter Channel",
                      frequency: "Enter Frequency",
                      purpose: "Type Here",
                    }}
                    errors={errors[stakeholder] || {}}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Box>
          {Object.keys(errors).length > 0 && (
            <Typography color="error" variant="body2">
              {/* Please fix the following errors: */}
            </Typography>
          )}
          <ul>
            {Object.entries(errors).map(([key, value]) =>
              typeof value === "object" ? (
                Object.entries(value).map(([field, message]) => (
                  <span
                    key={`${key}-${field}`}
                    style={{
                      lineHeight: "1.2",
                      margin: "2px 0",
                      color: "#d34040",
                      fontSize: "10px",
                      fontStyle: "italic",
                    }}
                  >
                    {message + " & "}
                  </span>
                ))
              ) : (
                <span
                  key={key}
                  style={{
                    lineHeight: "1.2",
                    margin: "2px 0",
                    color: "#d34040",
                    fontSize: "10px",
                    fontStyle: "italic",
                  }}
                >
                  {value}
                </span>
              ),
            )}
          </ul>
        </Box>
      </Grid>
      <BRSRFile
        {...{
          modalupload,
          setModalUpload,
          formData: formData1,
          setFormData: setFormData1,
          errors,
          setErrors,
          open,
          setOpen,
          Uploadkey: "identificationOfStakeholdersDoc",
          ml: 2,
          removeUpload,
        }}
      />
      {/* Submit Button */}
      {!hide && (
        <>
          {brsrReview ? (
            <Box sx={{ ml: "0.8rem", width: "99%" }}>
              <ReviewBox
                url={`/brsr/review/${id}`}
                handleSubmit={handleSubmit}
              />
            </Box>
          ) : (
            <Box textAlign="right" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                sx={{ bgcolor: "#1C486B", textTransform: "none", mt: 3, mb: 5 }}
                onClick={handleSubmit}
              >
                Save & Continue <EastIcon sx={{ ml: 2 }} />
              </Button>
            </Box>
          )}
        </>
      )}
      <Snackbar
        open={snackOpen.open}
        message={snackOpen.message}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setsnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          severity={snackOpen.severity || "info"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message || "No message provided"}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EssentialIndicatorsForm;
